import React from 'react';
import { useQuery } from 'react-query';
import {
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from '@mui/material';
import {
  Payment,
  Warning,
  Schedule,
  Receipt,
  Send,
  Visibility,
  Savings,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { financeAPI } from '../../services/api';

const FinanceDashboard = () => {
  const navigate = useNavigate();

  const { data: dueTodayRes, isLoading: dueTodayLoading } = useQuery(
    ['financeDueToday'],
    () => financeAPI.getDueTodayPayments({ page: 1, limit: 20 }),
    { staleTime: 60000 }
  );
  const { data: overdueRes, isLoading: overdueLoading } = useQuery(
    ['financeOverdue'],
    () => financeAPI.getOverduePayments({ page: 1, limit: 20, scope: 'overdue' }),
    { staleTime: 60000 }
  );
  const { data: subscriptionsRes, isLoading: subsLoading } = useQuery(
    ['financeSubscriptionsActive'],
    () => financeAPI.listSubscriptions({ page: 1, limit: 1, status: 'active' }),
    { staleTime: 60000 }
  );
  const { data: recentCollectionsRes, isLoading: recentLoading } = useQuery(
    ['financeRecentCollections'],
    () => financeAPI.getRecentCollections({ page: 1, limit: 20 }),
    { staleTime: 60000 }
  );

  const dueToday = dueTodayRes?.data?.data ?? [];
  const overdue = overdueRes?.data?.data ?? [];
  const dueTodayTotal = dueTodayRes?.data?.pagination?.total ?? dueToday.length;
  const overdueTotal = overdueRes?.data?.pagination?.total ?? overdue.length;
  const activeSubscriptionsTotal = subscriptionsRes?.data?.pagination?.total ?? 0;
  const recentCollections = recentCollectionsRes?.data?.data ?? [];

  const isLoading = dueTodayLoading || overdueLoading || subsLoading || recentLoading;

  const formatMoney = (amount, currency = '') => {
    if (amount == null) return '–';
    const n = Number(amount);
    return `${currency ? currency + ' ' : ''}${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`;
  };

  const StatCard = ({ title, value, icon, color, onClick }) => (
    <Card sx={{ height: '100%', cursor: onClick ? 'pointer' : 'default' }} onClick={onClick}>
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ bgcolor: color, borderRadius: 1, p: 1, color: 'white' }}>{icon}</Box>
          <Box>
            <Typography variant="h4" fontWeight="bold">
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 300 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Finance Dashboard
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Payments due today, overdue, and recent collections.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Payments due today"
            value={dueTodayTotal}
            icon={<Schedule />}
            color="#1976d2"
            onClick={() => navigate('/finance?view=due-today')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Overdue payments"
            value={overdueTotal}
            icon={<Warning />}
            color="#d32f2f"
            onClick={() => navigate('/finance?scope=overdue')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active subscriptions"
            value={activeSubscriptionsTotal}
            icon={<Receipt />}
            color="#2e7d32"
            onClick={() => navigate('/finance?status=active')}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ height: '100%', cursor: 'pointer' }} onClick={() => navigate('/finance')}>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Box sx={{ bgcolor: '#ed6c02', borderRadius: 1, p: 1, color: 'white' }}>
                  <Payment />
                </Box>
                <Box>
                  <Typography variant="body1" fontWeight="bold">
                    Record payment
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Quick action
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Grid container spacing={2}>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payments due today
              </Typography>
              <List dense>
                {dueToday.slice(0, 5).map((p) => (
                  <ListItem key={p.id}>
                    <ListItemText
                      primary={`${p.student_first_name || ''} ${p.student_last_name || ''}`.trim() || 'Student'}
                      secondary={`${p.due_date} • ${formatMoney(p.amount, p.currency)}`}
                    />
                    <ListItemSecondaryAction>
                      <Button size="small" onClick={() => navigate('/finance')}>
                        Record
                      </Button>
                      <Button size="small" onClick={() => navigate('/finance')}>
                        Remind
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {dueToday.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No payments due today.
                  </Typography>
                )}
                {dueTodayTotal > 5 && (
                  <Button size="small" onClick={() => navigate('/finance')}>
                    View all ({dueTodayTotal})
                  </Button>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Overdue payments
              </Typography>
              <List dense>
                {overdue.slice(0, 5).map((p) => (
                  <ListItem key={p.id}>
                    <ListItemText
                      primary={`${p.student_first_name || ''} ${p.student_last_name || ''}`.trim() || 'Student'}
                      secondary={`Due ${p.due_date} • ${formatMoney(p.amount, p.currency)}`}
                    />
                    <ListItemSecondaryAction>
                      <Button size="small" onClick={() => navigate('/finance')}>
                        Record
                      </Button>
                      <Button size="small" onClick={() => navigate('/finance')}>
                        Remind
                      </Button>
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
                {overdue.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No overdue payments.
                  </Typography>
                )}
                {overdueTotal > 5 && (
                  <Button size="small" onClick={() => navigate('/finance')}>
                    View all ({overdueTotal})
                  </Button>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Savings /> Recent collections
              </Typography>
              <List dense>
                {recentCollections.slice(0, 5).map((p) => (
                  <ListItem key={p.id}>
                    <ListItemText
                      primary={`${p.student_first_name || ''} ${p.student_last_name || ''}`.trim() || 'Student'}
                      secondary={p.paid_at ? `Collected ${p.paid_at} • ${formatMoney(p.amount, p.currency)}` : formatMoney(p.amount, p.currency)}
                    />
                  </ListItem>
                ))}
                {recentCollections.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No recent collections.
                  </Typography>
                )}
                {(recentCollectionsRes?.data?.pagination?.total ?? 0) > 5 && (
                  <Button size="small" onClick={() => navigate('/finance')}>
                    View all ({recentCollectionsRes.data.pagination.total})
                  </Button>
                )}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
        <Button variant="contained" startIcon={<Payment />} onClick={() => navigate('/finance')}>
          Record payment
        </Button>
        <Button variant="outlined" startIcon={<Send />} onClick={() => navigate('/finance')}>
          Send reminder
        </Button>
        <Button variant="outlined" startIcon={<Visibility />} onClick={() => navigate('/finance')}>
          View invoices
        </Button>
      </Box>
    </Box>
  );
};

export default FinanceDashboard;
